import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { ArrowBack, Delete, Edit } from '@mui/icons-material';

import { AdminRoles } from '../config/DiactiveTypes';
import { getUserTypeConfig } from '../config/UserTypes';
import DetailRow from '../components/common/DetailRow';
import ConfirmDeleteDialog from '../components/common/ConfirmDeleteDialog';

const UserDetails = ({ userType, role }) => {
  const cfg = getUserTypeConfig(userType);
  const { userId, tabId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const allTabs = useMemo(
    () => [
      { id: 'data', label: cfg.labels.dataTab ?? `Datos del ${cfg.labels.singular}` },
      ...(cfg.extraTabs ?? []),
    ],
    [cfg]
  );
  const tabIndexById = useMemo(
    () => Object.fromEntries(allTabs.map((t, i) => [t.id, i])),
    [allTabs]
  );

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState(
    tabId && tabIndexById[tabId] !== undefined ? tabIndexById[tabId] : 0
  );
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  const canResolve = AdminRoles.includes(role);
  const canEditOrDelete = cfg.permissions.canEditOrDelete(role);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setErrorMessage(null);
      try {
        const data = await cfg.service.getById(userId);
        if (!data) throw new Error(cfg.labels.notFound(userId));
        if (!cfg.roles.includes(data.role)) throw new Error(cfg.labels.invalid(userId));
        setUser(data);
      } catch (error) {
        setErrorMessage(error.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [cfg, userId]);

  const handleBack = () => {
    if (location.state?.fromFilteredTable) {
      navigate(-1);
    } else {
      navigate(cfg.routes.list());
    }
  };

  const handleEdit = () => navigate(cfg.routes.edit(userId));

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await cfg.service.remove(userId);
      setOpenDeleteDialog(false);
      navigate(cfg.routes.list(), {
        state: { successMessage: `El ${cfg.labels.singular} se ha eliminado correctamente` },
      });
    } catch (error) {
      setOpenDeleteDialog(false);
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Container sx={{ pt: 4 }}>
        <Alert severity="error">
          <AlertTitle>Error</AlertTitle>
          {errorMessage}
        </Alert>
      </Container>
    );
  }

  const ctx = { role, canResolve, canEditOrDelete };
  const extraTabs = cfg.extraTabs ?? [];

  return (
    <Container maxWidth="lg" sx={{ pt: 3, pb: 3 }}>
      <Snackbar
        open={snackbar.open}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ mt: 10 }}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <ConfirmDeleteDialog
        open={openDeleteDialog}
        onCancel={() => setOpenDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        title={cfg.labels.deleteTitle}
        description={cfg.labels.deleteDescription}
        loading={deleting}
      />
      <Paper elevation={3} sx={{ padding: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={handleBack} aria-label="back">
            <ArrowBack color="primary" sx={{ fontSize: 40 }} />
          </IconButton>
          <Typography variant="h5" component="h1" sx={{ ml: 1, flex: 1 }}>
            {user.fullName}
          </Typography>
          {canEditOrDelete && (
            <>
              <IconButton onClick={handleEdit} aria-label="edit">
                <Edit color="primary" />
              </IconButton>
              <IconButton onClick={() => setOpenDeleteDialog(true)} aria-label="delete">
                <Delete color="error" />
              </IconButton>
            </>
          )}
        </Box>

        <Tabs value={tab} onChange={(_, newValue) => setTab(newValue)} sx={{ mb: 3 }}>
          {allTabs.map((t) => (
            <Tab key={t.id} label={t.label} />
          ))}
        </Tabs>

        {tab === 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: { xs: 0, md: 6 },
            }}
          >
            {cfg.detailRows.map((column, i) => (
              <Box key={i}>
                {column.map((row) => (
                  <DetailRow key={row.label} label={row.label} value={row.get(user)} />
                ))}
              </Box>
            ))}
          </Box>
        )}

        {extraTabs.map((t, i) =>
          tab === i + 1 ? <Box key={t.id}>{t.render(user, ctx)}</Box> : null
        )}
      </Paper>
    </Container>
  );
};

export default UserDetails;
